import { countLabel } from '@/utils/plural'
import { getErrorMessage, isConflict } from '@lib/apiError'
import {
	BottomBar,
	StepIndicatorState,
	daysLabel,
	daysSummary,
	joinedNames,
	startDateLabel,
	stepState,
} from '@pages/Trips/CreateTripFlow/core/presentation'
import {
	DestinationEvents,
	EditSaveRequest,
	EditTripDestinationsState,
	RemovedRowEvents,
	hasChanges,
	hasEventConflicts,
	saveRequest,
	seededHadDestinations,
	totalTripDays,
} from './editState'

/**
 * Every label the edit-destinations screens derive from the edit state.
 * Produces the create flow's bottom-bar shape so the shared shell renders
 * either flow; mirrors the iOS flow's `EditTripDatesPresentation`.
 */

/** The two pages of the flow, in order — the create flow's first two. */
export type EditTripStep = 'where' | 'when'

export const EDIT_STEPS: EditTripStep[] = ['where', 'when']

/** Short name for the indicator segment. */
export const editStepLabel = (step: EditTripStep): string =>
	step === 'where' ? 'Destinations' : 'Start Date'

/** Whether a step's own work is done — completed versus skipped past. */
const isSatisfied = (state: EditTripDestinationsState, step: EditTripStep): boolean =>
	step === 'where' ? state.draft.destinations.length > 0 : state.draft.startDate !== null

/**
 * Same rule as the create flow: the last step is reached by finishing, never
 * by jumping — so here Start Date is unreachable from Destinations, while
 * Destinations stays tappable from Start Date.
 */
export const editStepIndicatorState = (
	state: EditTripDestinationsState,
	step: EditTripStep,
	current: EditTripStep,
): StepIndicatorState =>
	stepState({
		index: EDIT_STEPS.indexOf(step),
		currentIndex: EDIT_STEPS.indexOf(current),
		isLast: step === EDIT_STEPS[EDIT_STEPS.length - 1],
		isSatisfied: isSatisfied(state, step),
	})

// MARK: Bottom bar

/**
 * `events` is the draft's event projection (see `destinationEvents`): while it
 * shows a conflict the flow stops where the fix is — Next is refused on
 * Destinations, and Save on Start Date — instead of by the server's 409 at
 * the end.
 */
export const editBottomBar = (
	state: EditTripDestinationsState,
	step: EditTripStep,
	events: DestinationEvents,
): BottomBar => {
	const { draft } = state
	const hasPlaces = draft.destinations.length > 0
	const days = totalTripDays(state)
	const isLast = step === 'when'
	const blockedByEvents = hasEventConflicts(events)

	let title: string
	if (hasPlaces) {
		title = joinedNames(draft)
	} else if (days > 0) {
		// Legacy trip carrying days but no destinations — its length is all
		// the bar has to name it by.
		title = daysLabel(days)
	} else {
		title = 'No places yet'
	}

	let subtitle: string
	if (blockedByEvents) {
		subtitle = isLast
			? 'Some removed days still hold events — fix that under Destinations'
			: 'Some removed days still hold events'
	} else if (hasPlaces) {
		subtitle = daysSummary(draft, days)
	} else if (seededHadDestinations(state)) {
		subtitle = 'Keep at least one place'
	} else if (step === 'where') {
		subtitle = "Add the places you're visiting"
	} else {
		const start = startDateLabel(draft)
		subtitle = start ? `Starting on ${start}` : 'No starting date yet'
	}

	return {
		title,
		subtitle,
		ctaTitle: isLast ? 'Save' : 'Next',
		// Neither step can be passed over here — the edit always commits.
		ctaStyle: 'primary',
		ctaEnabled: !blockedByEvents && (!isLast || (hasChanges(state) && saveRequest(state) !== null)),
		showsBack: isLast && !hasPlaces,
	}
}

// MARK: Row copy

/** "No events" / "1 event" / "3 events" — what a destination holds today. */
export const rowEventsLabel = (count: number): string =>
	count === 0 ? 'No events' : countLabel(count, 'event')

/** The error under a row that has trimmed days its events still sit on. */
export const strandedEventsMessage = (count: number): string =>
	`${countLabel(count, 'event')} on the trimmed days. Move or delete the ${count === 1 ? 'event' : 'events'} first.`

/**
 * Why a row's Remove was refused: the destination still holds events, and the
 * server would refuse the save anyway. Null when the row can go.
 */
export const removeRefusalMessage = (name: string, count: number): string | null =>
	count === 0
		? null
		: `${name} still holds ${countLabel(count, 'event')}. Move or delete ${count === 1 ? 'it' : 'them'} before removing it.`

/**
 * The notice above the list for destinations removed while holding events:
 * the row is gone, so the list says it. Null when there are none.
 */
export const removedEventsMessage = (removed: RemovedRowEvents[]): string | null =>
	removed.length === 0
		? null
		: removed
				.map(
					({ name, eventCount }) =>
						`${name} still holds ${countLabel(eventCount, 'event')}. Add it back, or move or delete ${eventCount === 1 ? 'it' : 'them'} first.`,
				)
				.join(' ')

// MARK: Error copy

/**
 * User-facing message for a failed save. The 409 copy depends on which call
 * the edit mapped onto: the destinations PUT rolls the whole edit back when
 * removed days still hold events, while the legacy tripDates fallback
 * conflicts on events falling outside the new dates.
 */
export const saveErrorMessage = (error: unknown, request: EditSaveRequest): string => {
	if (isConflict(error)) {
		return request.kind === 'destinations'
			? 'Some of the days you removed still have events scheduled. Move or delete those events first.'
			: 'Some events occur outside the new trip dates. Adjust or remove them before changing the dates.'
	}
	return getErrorMessage(error, 'Failed to update your trip. Please try again.')
}
